import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, type Memo } from '../lib/api'
import { today } from '../lib/format'
import { Panel, Alert, Loading, Empty, Tag } from '../components/ui'

const STATUSES = ['草稿', '待審核', '已生效', '已終止']

/** 剩餘天數：到期日相對今天，用來標紅／標橘 */
function expiryText(to: string | null, status: string) {
  if (status !== '已生效' || !to) return { text: '—', color: undefined as string | undefined }
  const days = Math.round((new Date(to).getTime() - new Date(today()).getTime()) / 86400000)
  if (days < 0) return { text: `已過期 ${-days} 天`, color: 'var(--bad)' }
  if (days <= 90) return { text: `${days} 天`, color: 'var(--warn)' }
  return { text: `${days} 天`, color: undefined }
}

export default function Memos() {
  const [rows, setRows] = useState<Memo[] | null>(null)
  const [error, setError] = useState('')
  const [msg, setMsg] = useState('')
  const [role, setRole] = useState('')
  const [status, setStatus] = useState('')
  const [q, setQ] = useState('')
  const isAdmin = role === 'admin'

  const load = () => {
    setRows(null)
    const qs = status ? `?status=${encodeURIComponent(status)}` : ''
    api.get<Memo[]>(`/memos${qs}`).then(setRows).catch((e) => setError(e.message))
  }

  useEffect(load, [status])
  useEffect(() => {
    api.get<{ user: { role: string } }>('/me').then((d) => setRole(d.user.role)).catch(() => {})
  }, [])

  /* 搜尋在前端做：備忘錄一位寫手一份，總量就是寫手數量，不會多到需要後端分頁 */
  const shown = useMemo(() => {
    const kw = q.trim().toLowerCase()
    if (!kw) return rows ?? []
    return (rows ?? []).filter(
      (m) =>
        (m.writer_name ?? '').toLowerCase().includes(kw) ||
        (m.party_b_title ?? '').toLowerCase().includes(kw)
    )
  }, [rows, q])

  const act = async (m: Memo, kind: 'submit' | 'terminate' | 'delete') => {
    const texts = {
      submit: `確定送出「${m.writer_name}」的備忘錄審核？\n\n送出後由管理員核准才會生效。`,
      terminate: `確定終止「${m.writer_name}」的備忘錄？\n\n終止後這份合約不再列入到期提醒，且無法復原。`,
      delete: `確定刪除「${m.writer_name}」的備忘錄草稿？\n\n此動作無法復原。`,
    }
    if (!confirm(texts[kind])) return
    setError(''); setMsg('')
    try {
      if (kind === 'delete') await api.del(`/memos/${m.id}`)
      else await api.post(`/memos/${m.id}/${kind}`)
      setMsg(
        kind === 'submit' ? `已送出「${m.writer_name}」的備忘錄審核`
        : kind === 'terminate' ? `已終止「${m.writer_name}」的備忘錄`
        : `已刪除草稿`
      )
      load()
    } catch (e: any) {
      setError(e.message)
    }
  }

  return (
    <>
      <div className="page-head">
        <h2>合作備忘錄</h2>
        {rows && <span className="hint">共 {shown.length} 份</span>}
        <div className="spacer" />
        <Link to="/memos/new"><button className="primary">新增備忘錄</button></Link>
      </div>

      <div className="btn-row" style={{ marginBottom: 12 }}>
        <input
          style={{ width: 200 }}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="搜尋寫手／乙方名稱"
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 120 }}>
          <option value="">全部狀態</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <Alert kind="error">{error}</Alert>
      {msg && <div className="alert ok">{msg}</div>}

      <Panel>
        {!rows ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>{status ? `沒有「${status}」的備忘錄` : '還沒有任何備忘錄'}</Empty>
        ) : shown.length === 0 ? (
          <Empty>共 {rows.length} 份，但沒有符合搜尋條件的</Empty>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>寫手</th>
                  <th>乙方名稱</th>
                  <th>簽約日</th>
                  <th>合約期限</th>
                  <th className="num">到期</th>
                  <th>狀態</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {shown.map((m) => {
                  const exp = expiryText(m.effective_to, m.status)
                  return (
                    <tr key={m.id} style={m.status === '已終止' ? { opacity: 0.55 } : undefined}>
                      <td><Link to={`/memos/${m.id}`}>{m.writer_name}</Link></td>
                      <td>{m.party_b_title ?? m.writer_name}</td>
                      <td>{m.signed_on ?? '—'}</td>
                      <td>
                        {m.effective_from ?? '—'} ～ {m.effective_to ?? '—'}
                      </td>
                      <td className="num" style={exp.color ? { color: exp.color } : undefined}>
                        {exp.text}
                      </td>
                      <td>
                        <Tag value={m.status} />
                        {/* 被退回的會回到草稿並帶著原因 */}
                        {m.status === '草稿' && m.review_note && (
                          <span
                            title={`${m.reviewed_at} 被 ${m.reviewed_by} 退回：${m.review_note}`}
                            style={{ color: 'var(--bad)', fontSize: 11, marginLeft: 4 }}
                          >
                            已退回
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="link-actions">
                          <Link to={`/memos/${m.id}/print`}>列印</Link>
                          {m.status === '草稿' && (
                            <button className="primary" onClick={() => act(m, 'submit')}>送出審核</button>
                          )}
                          {isAdmin && m.status === '已生效' && (
                            <button onClick={() => act(m, 'terminate')}>終止</button>
                          )}
                          {m.status === '草稿' && (
                            <button className="danger" onClick={() => act(m, 'delete')}>刪除</button>
                          )}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="hint" style={{ marginBottom: 0, fontSize: 12, color: 'var(--ink-soft)' }}>
          草稿 →（送出審核）→ 待審核 →（管理員核准）→ 已生效。核准新的一份時，同一位寫手先前生效中的會自動終止。
        </p>
      </Panel>
    </>
  )
}
